import { DietType } from 'src/gqlReactTypings.generated.d';
import { PreferencesForm } from 'src/pages/consumer/meal-plans/meal-plan-intake/preferences/types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createSelectors } from './create-selectors';

interface Actions {
  setFormData: (values: PreferencesForm) => void;
  clearFormData: () => void;
  getFormData: () => PreferencesForm;
  isFormComplete: () => boolean;
}

interface State {
  formData: PreferencesForm;
}

const INITIAL_FORM_DATA: PreferencesForm = {
  adults: 0,
  bigEaters: 0,
  littleEaters: 0,
  deliveryDate: undefined,
  deliveryWindowId: undefined,
  dietType: DietType.Omnivore,
  proteinPreferences: [],
  anyDietaryRestrictions: false,
  replacementDietaryRestrictions: [],
  menuDietaryRestrictions: [],
  cuisinePreferences: [],
  moreInfo: undefined,
  mealsPerWeek: undefined,
  mealPlanType: undefined,
  deliveryTimeWindowStart: undefined,
  deliveryTimeWindowEnd: undefined,
  reusablePackaging: undefined,
  optedIntoReusablePackagingAtIntake: undefined,
  intakeCompletedAtDate: undefined,
  intakeCompletedAt: undefined,
  householdPersona: undefined,
  intakeSkippedAll: undefined,
  householdGoals: [],
};

const INITIAL_STATE = {
  formData: INITIAL_FORM_DATA,
};

/**
 * Backfills {@link PreferencesForm.optedIntoReusablePackagingAtIntake} from the persisted
 * `reusablePackaging` selection for intakes stored before that field existed, so the cart/checkout
 * packaging section keeps rendering for users who chose stainless steel pre-deploy.
 */
export const migrateMealPlanIntakeState = (persistedState: unknown): State & Actions => {
  const state = persistedState as State & Actions;
  const { formData } = state ?? {};
  if (formData != null && formData.optedIntoReusablePackagingAtIntake === undefined) {
    return {
      ...state,
      formData: { ...formData, optedIntoReusablePackagingAtIntake: formData.reusablePackaging },
    };
  }
  return state;
};

export const useMealPlanIntakeStore = create(
  persist<State & Actions>(
    (set, get) => ({
      ...INITIAL_STATE,
      // nit: technically updateFormData, we can migrate if we need both functions later @jonathan 3/10
      setFormData: (values) => set({ formData: { ...get().formData, ...values } }),
      clearFormData: () => set({ formData: INITIAL_FORM_DATA }),
      getFormData: () => get().formData,
      isFormComplete: () => {
        const { mealPlanType, deliveryDate, dietType, deliveryWindowId } = get().formData;
        return !!dietType && !!mealPlanType && !!deliveryDate && !!deliveryWindowId;
      },
    }),
    {
      name: 'meal-plan-intake-store',
      getStorage: () => localStorage,
      version: 1,
      migrate: migrateMealPlanIntakeState,
    }
  )
);

export const useMealPlanIntakeStoreSelectors = createSelectors(useMealPlanIntakeStore);
